import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";

import { useValidateCurrencyRules } from "@bentolabs/kit/entities";
import { useBalanceQuery } from "@bentolabs/kit/hooks";
import { formatCurrency } from "@bentolabs/kit/web3";
import invariant from "tiny-invariant";
import { useLocalStorage } from "usehooks-ts";
import { formatUnits, parseUnits } from "viem";
import { useChainId } from "wagmi";

import { useUsdRate } from "entities/balance/use-usd-rate";
import { CurrencyLabel } from "entities/currency";
import { RHFInputCurrency } from "features/input-currency";
import { SelectCurrency } from "features/select-currency";
import { useDepositMutation } from "shared/api/use-deposit-mutation";
import {
  createLatestPricesSelect,
  useLatestPricesQuery,
} from "shared/api/use-latest-price-query";
import { bentoUSDConfig, Currency, tellerConfig } from "shared/config/const";
import { Table } from "shared/ui/table";
import { div, mul } from "shared/utils";
import { adjustPriceDown } from "shared/utils/adjust-price";
import { scaleDecimals } from "shared/utils/scale-decimals";
import { useTrade } from "widgets/trade/context";
import { useDepositFee } from "widgets/trade/hooks/use-deposit-fee";

import { DepositFormType } from "../../types";
import { calculateMinAmount } from "../../utils";
import { LabelWithTabs } from "../label-with-tabs";
import { SubmitButton } from "../submit-button";
import { TransactionInfo } from "../transaction-info";

type Props = {
  currencies: Currency[];
};

export const DepositForm = ({ currencies }: Props) => {
  const { theme, slippage } = useTrade();
  const chainId = useChainId();
  const bento = bentoUSDConfig[chainId];
  const [latestOptions] = useLocalStorage<Currency[]>("latest-options", []);
  const initialFromCurrency = latestOptions[0] ?? currencies[0];
  const form = useForm<DepositFormType>({
    mode: "onChange",
    defaultValues: {
      fromCurrency: { ...initialFromCurrency },
      toCurrency: { ...bento },
    },
  });
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    trigger,
    formState,
  } = form;
  const { isValid } = formState;

  const fromCurrency = useWatch({ control, name: "fromCurrency" });
  const fromValue = useWatch({ control, name: "fromValue" });
  const toCurrency = useWatch({ control, name: "toCurrency" });
  const toValue = useWatch({ control, name: "toValue" });

  const toUsdValue = useUsdRate(toCurrency, toValue);

  const rate = toValue && fromValue ? div(toValue, fromValue) : undefined;
  const minimumReceive =
    toValue && slippage
      ? formatCurrency(mul(toValue, slippage).toString(), {
          decimalScale: 4,
        })
      : undefined;

  const fromCurrencyBalanceQuery = useBalanceQuery({ currency: fromCurrency });
  const toCurrencyBalanceQuery = useBalanceQuery({ currency: toCurrency });

  const fromCurrencyRules = useValidateCurrencyRules({
    currency: fromCurrency,
    spenderContractAddress: tellerConfig[chainId],
  });

  const latestPriceQuery = useLatestPricesQuery({
    enabled: Boolean(fromCurrency),
    select: createLatestPricesSelect(chainId, adjustPriceDown),
  });

  const depositMutation = useDepositMutation();
  const depositFee = useDepositFee({ fromCurrency, fromValue, toCurrency });

  const getAssetPrice = () => {
    invariant(latestPriceQuery.data, "latestPriceQuery.data is undefined");
    invariant(fromCurrency?.symbol, "fromCurrency.symbol is undefined");
    return latestPriceQuery.data[fromCurrency.symbol];
  };

  const handleFromCurrencyChange = (newCurrency: Currency) => {
    reset({
      fromCurrency: { ...newCurrency },
      fromValue: "",
      toCurrency: { ...bento },
      toValue: "",
    });
  };

  const handleFromValueChange = (value: string) => {
    if (!value || !fromCurrency) {
      setValue("toValue", "");
      return;
    }
    const price = getAssetPrice();
    console.log(fromCurrency.decimals);
    console.log(toCurrency.decimals);
    console.log(value);
    const scaledValue = scaleDecimals(
      parseUnits(value, fromCurrency.decimals),
      BigInt(fromCurrency.decimals),
      BigInt(toCurrency.decimals)
    );
    console.log("scaledValue: ", scaledValue);
    const val = formatUnits(BigInt(scaledValue), toCurrency.decimals);
    console.log("val: ", val);
    const toValue = formatCurrency(mul(val, price.formatted).toString());

    setValue("toValue", toValue);
  };

  const handleSubmitSuccess: SubmitHandler<DepositFormType> = (data) => {
    invariant(fromCurrency, "");
    invariant(fromValue, "");

    const minimumAmount = calculateMinAmount(data.fromValue, slippage);
    depositMutation.mutate(
      {
        amount: fromValue,
        currency: fromCurrency,
        minimumAmount,
      },
      {
        onSuccess: () => {
          reset({ ...getValues(), fromValue: "", toValue: "" });
        },
      }
    );
  };

  const approvalCurrencies = fromCurrency
    ? [{ ...fromCurrency, value: fromValue }]
    : undefined;

  return (
    <FormProvider {...form}>
      <div className="flex flex-col px-2.5">
        <RHFInputCurrency
          label={
            <LabelWithTabs
              value={fromValue}
              currency={fromCurrency}
              onChange={(value) => {
                setValue("fromValue", value);
              }}
            >
              From
            </LabelWithTabs>
          }
          rules={fromCurrencyRules}
          control={control}
          disabled={!fromCurrency}
          name="fromValue"
          bottomLabel="Balance"
          bottomValue={fromCurrencyBalanceQuery.data?.formatted}
          decimals={fromCurrency?.decimals}
          onChange={handleFromValueChange}
          slot={
            <SelectCurrency
              currency={fromCurrency}
              onChange={handleFromCurrencyChange}
              options={currencies}
            />
          }
        />
        <RHFInputCurrency
          control={control}
          disabled
          name="toValue"
          className="mt-2"
          label="From"
          usdValue={toUsdValue}
          bottomLabel="Balance"
          bottomValue={toCurrencyBalanceQuery.data?.formatted}
          decimals={toCurrency.decimals}
          slot={<CurrencyLabel disabled {...toCurrency} />}
        />

        <TransactionInfo
          className="mt-6.5"
          slot={
            <div>
              1 USDC ={" "}
              {formatCurrency(rate?.toString() ?? "1", { decimalScale: 4 })}{" "}
              bentoUSD
            </div>
          }
        >
          <div className="bg-nobel/5 flex flex-col px-4.5 py-4">
            <Table>
              <Table.Item label="Method:" value="Swap" />
              <Table.Item
                label="Receive at least:"
                value={`${minimumReceive ?? 0} ${bento.symbol}`}
              />
              <Table.Item label="Max Slippage:" value={`${slippage}%`} />
              <Table.Item label="Gas fee:" value={depositFee.feeInUsd ?? "-"} />
            </Table>
          </div>
        </TransactionInfo>
      </div>

      <SubmitButton
        variant={theme}
        className="mt-6.5 w-full"
        disabled={!isValid || !fromCurrency}
        approvalCurrencies={approvalCurrencies}
        spenderAddress={bentoUSDConfig[chainId].address}
        onApproveSuccess={() => {
          setTimeout(() => {
            trigger("fromValue");
          }, 200);
        }}
        onSubmit={handleSubmit(handleSubmitSuccess)}
      >
        Deposit
      </SubmitButton>
    </FormProvider>
  );
};
